import React, { Component, useEffect, useRef } from 'react'
import { Animated, Easing, GestureResponderEvent, Pressable, View, ViewStyle } from 'react-native'
import NativeLinearGradient from 'react-native-linear-gradient'

// Wrapper component required for Animated.createAnimatedComponent
class LinearGradientWrapper extends Component<any> {
  render() {
    // @ts-ignore
    return <NativeLinearGradient {...this.props} />
  }
}

// Create animated component using React Native Animated (not Reanimated)
// @ts-ignore
const AnimatedLinearGradient = Animated.createAnimatedComponent(LinearGradientWrapper)

interface AnimatedGradientProgressProps {
  /**
   * Final value to animate to
   */
  value: number
  useAngle?: boolean
  /**
   * Starting value to animate from
   * @default 0
   */
  initialValue?: number
  angle?: number
  animateDuration?: number
  /**
   * Animation start delay in ms
   * @default 800
   */
  delay?: number
  leftIconStyle?: ViewStyle
  rightIconStyle?: ViewStyle
  colors?: string[]
  locations?: number[]
  leftIcon?: React.ReactElement | null
  rightIcon?: React.ReactElement | null
  style?: ViewStyle
  progressbarStyle?: any
  leftPress?: () => void
  rightPress?: (event: GestureResponderEvent) => void
}

export const AnimatedGradientProgress = (props: AnimatedGradientProgressProps) => {
  const progress = useRef(new Animated.Value(0)).current
  const animationRef = useRef<Animated.CompositeAnimation | null>(null)

  const {
    style,
    value,
    useAngle,
    angle,
    leftIconStyle,
    rightIconStyle,
    colors,
    leftIcon,
    locations,
    progressbarStyle,
    leftPress,
    rightPress,
    rightIcon,
    animateDuration = 800,
    delay = 500,
    initialValue = 0,
  } = props

  useEffect(() => {
    // Cancel any in-flight animation
    if (animationRef.current) {
      animationRef.current.stop()
    }

    // Reset to initial value
    progress.setValue(initialValue)

    // Create and start new animation
    animationRef.current = Animated.timing(progress, {
      useNativeDriver: false,
      toValue: value,
      duration: animateDuration,
      delay: delay,
      easing: Easing.bezier(0.25, 0.1, 0.25, 1), // Smooth easing similar to elastic
    })

    animationRef.current.start()

    // Cleanup on unmount or before re-running
    return () => {
      if (animationRef.current) {
        animationRef.current.stop()
      }
    }
  }, [value, initialValue, animateDuration, delay])

  const interpolatedProgress = progress.interpolate({
    inputRange: [0, 1],
    outputRange: ['0%', '100%'],
  })

  return (
    <View style={{ ...CONTAINER, ...style }}>
      <View style={{ ...LEFT_ICON, ...leftIconStyle }}>
        <Pressable disabled={!leftPress} onPress={leftPress}>
          {leftIcon}
        </Pressable>
      </View>
      <Animated.View style={{ width: interpolatedProgress, maxWidth: '100%' }}>
        <AnimatedLinearGradient
          style={{ ...GRADIENT, ...progressbarStyle }}
          colors={colors ?? ['#967CFD', '#3177FF']}
          locations={locations ?? [0, 1]}
          useAngle={useAngle}
          angle={angle ?? 180}
          pointerEvents="none"
        />
      </Animated.View>
      <View style={{ ...RIGHT_ICON, ...rightIconStyle }}>
        <Pressable disabled={!rightPress} onPress={rightPress}>
          {rightIcon}
        </Pressable>
      </View>
    </View>
  )
}

const CONTAINER: ViewStyle = { backgroundColor: '#EDEFF1', padding: 6, borderRadius: 100 }
const GRADIENT: ViewStyle = { width: '100%', borderRadius: 100, height: 30 }
const LEFT_ICON: ViewStyle = { position: 'absolute', left: 6, top: 6, zIndex: 999 }
const RIGHT_ICON: ViewStyle = { position: 'absolute', right: 6, top: 6, zIndex: 999 }
